"use client";

import { useCallback } from "react";

import cn from "classnames";

import { ChevronDownIcon } from "@/components/icons/chevronDown.icon";
import { Text, type TextProps } from "@/components/text/text.component";

import { brown } from "@/app/font";
import { Theme } from "@/sanity.types";

export const Accordion = ({
  title,
  titleVariant = "h6",
  children,
  arrowSize = 24,
  isExpanded = false,
  arrowClassName,
  isCollapsible = true,
  theme = "primary",
  name,
  onToggle,
  flush = false,
  summaryClassName,
  contentClassName,
}: AccordionProps) => {
  const handleToggle = useCallback(
    (e: React.SyntheticEvent<HTMLDetailsElement>) => {
      onToggle?.(e.currentTarget.open);
    },
    [onToggle],
  );

  const handleSummaryClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (name && isExpanded) {
        e.preventDefault();
      }
    },
    [name, isExpanded],
  );

  if (!title) return null;

  return (
    <details
      onToggle={handleToggle}
      open={isCollapsible ? isExpanded : true}
      name={name}
      data-testid="accordion-item"
      className={cn("group border-b-[0.1px] py-4", {
        "mx-6": !flush,
        "mx-0": flush,
        "border-primary": theme === "secondary" || theme === "tertiary",
        "border-greyscale-strength": theme === "light",
        "border-white": theme === "primary",
      })}>
      <summary
        onClick={handleSummaryClick}
        className={cn(
          brown.className,
          "flex list-none items-center justify-between py-2 group-not-first:py-4 group-open:pb-0 [&::-webkit-details-marker]:hidden [&::marker]:hidden",
          "focus-visible:ring-secondary rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          {
            "pointer-events-none select-none": !isCollapsible,
            "cursor-pointer": isCollapsible,
            "text-primary": theme === "secondary",
            "text-white": theme === "primary",
            "text-greyscale-headline": theme === "tertiary",
            "text-greyscale-body": theme === "light",
          },
          summaryClassName,
        )}>
        {typeof title === "string" ? (
          <Text variant={titleVariant} component="span" className="mr-8">
            {title}
          </Text>
        ) : (
          title
        )}

        {isCollapsible && (
          <ChevronDownIcon
            width={arrowSize}
            height={arrowSize}
            className={cn(
              arrowClassName,
              "stroke-greyscale-body shrink-0 transition-transform duration-300 group-open:rotate-180",
            )}
            aria-hidden
          />
        )}
      </summary>

      <div
        className={cn(
          "pt-2 pb-6",
          {
            "text-greyscale-body": theme === "tertiary",
          },
          contentClassName,
        )}
        data-testid="accordion-content">
        {children}
      </div>
    </details>
  );
};

export type AccordionProps = {
  title: React.ReactNode;
  titleVariant?: TextProps["variant"];
  children: React.ReactNode;
  isExpanded?: boolean;
  isCollapsible?: boolean;
  arrowClassName?: string;
  arrowSize?: number;
  onToggle?: (isOpen: boolean) => void;
  theme?: Theme;
  name?: string;
  flush?: boolean;
  summaryClassName?: string;
  contentClassName?: string;
};
